import { useTranslation } from 'react-i18next'
import TileBlur from '@ui/tiles/TileBlur'
import Gradient from '@ui/effects/Gradient'
import { colors as c } from '@ui/colors'
import { PaymentDetails } from '@order/hooks/usePayments'
import { usePaymentMethodSettings } from '@order/hooks/usePaymentMethods'
import { View } from 'react-native'
import QRCode from 'react-qr-code'
import StatusBadge from './StatusBadge'

import terminalImg from '@/images/payment/terminal.avif'
import installmentImg from '@/images/payment/installment.avif'
import paylinkImg from '@/images/payment/paylink.avif'
import invoiceImg from '@/images/payment/invoice.avif'

import { PaymentMethod } from '@ca/common'
import cn from '@ui/utils/cn'
import usePaymentTranslations from '@order/hooks/usePaymentTranslations'

const PaymentImages: Record<PaymentMethod, any> = {
  terminal: terminalImg,
  monthly: installmentImg,
  paylink: paylinkImg,
  invoice: invoiceImg,
}

interface Props {
  payment: PaymentDetails
  children?: React.ReactNode
}

export default function StatusInfo({ payment, children }: Props) {
  const { t } = useTranslation(['payment', 'ui'])
  const settings = usePaymentMethodSettings(payment.method, {
    provider: payment.provider,
  })
  const { status } = payment
  const qrCode = settings.qrcode && !!payment.link

  const image = PaymentImages[payment.method]

  const { key, providerName } = usePaymentTranslations({
    method: payment.method,
    provider: payment.provider,
  })

  const title = t(`payment:status.info.${key}.title`, {
    provider: providerName,
  })

  const desc = t(`payment:status.info.${key}.status.${status}`, {
    provider: providerName,
  })

  return (
    <TileBlur.Provider variant="option">
      <TileBlur.Glass
        image={!qrCode ? image : undefined}
        imageStyle={{
          transform: [{ translateX: -25 }, { translateY: 0 }],
        }}
        blurStyle={{
          opacity: 0.9,
          transform: [
            { translateX: 100 },
            { translateY: -25 },
            { rotate: '15deg' },
            { scale: 0.75 },
          ],
        }}
        gradient={
          <Gradient
            colors={!qrCode ? [c.gray[50], c.white0] : [c.gray[50], c.white]}
            start={[0, 0.5]}
            end={[1, 0.5]}
          />
        }
        style={{ height: 'auto' }}
      >
        <View className="flex w-full flex-col gap-0">
          <View className="flex flex-row px-8 py-8" style={{ minHeight: 224 }}>
            <View
              className="flex-1 flex-col justify-between gap-4"
              // style={{ height: '100%' }}
              // style={{ height: 224, maxHeight: 224 }}
            >
              <TileBlur.Title
                title={title}
                desc={desc}
                classNameDesc={cn(qrCode && 'w-full')}
              />
              <View className="flex flex-row items-center justify-start">
                <StatusBadge status={status} />
              </View>
            </View>
            {qrCode && payment.link && (
              <View className="pr-2">
                <View className="relative w-[160px] justify-center bg-white">
                  <QRCode size={160} value={payment.link} />
                </View>
              </View>
            )}
          </View>
          {children}
        </View>
      </TileBlur.Glass>
    </TileBlur.Provider>
  )
}
